import { Model } from "@vuex-orm/core";
import { parseISO } from "date-fns";
import { isFutureDate, isPastDate } from "@/utils/eproDate";

export const VARIANT_MAPPING_BADGE = Object.freeze({
  ACTIVE: "success",
  ENDED: "base",
  PAUSED: "warning",
  RESUME: "info",
  STOPPED: "base",
  ASSIGNED: "neutral",
});

export const VARIANT_MAPPING_BUTTON = Object.freeze({
  ACTIVE: "success",
  PAUSED: "warning",
  ENDED: "base",
  STOPPED: "base",
  ASSIGNED: "neutral",
  PRIMARY: "primary",
});

export const PROGRAM_STATUS = Object.freeze({
  active: "ACTIVE",
  ended: "ENDED",
  paused: "PAUSED",
  resume: "RESUME",
  stopped: "STOPPED",
  assigned: "ASSIGNED",
});

export const PROGRAM_ACTIONS = Object.freeze({
  END: "end",
  PAUSE: "pause",
  RESUME: "resume",
  STOP: "stop",
  EDIT_DURATION: "duration",
  PROGRAM_DETAIL: "detail",
});

function calculateProgramStatus(program) {
  if (
    program.stopped ||
    (program.endsOn && isPastDate(parseISO(program.endsOn)))
  ) {
    return PROGRAM_STATUS.ended;
  }
  if (program.startsOn && isFutureDate(parseISO(program.startsOn))) {
    return PROGRAM_STATUS.assigned;
  }
  if (program.paused) {
    return PROGRAM_STATUS.paused;
  }
  return PROGRAM_STATUS.active;
}

const STATUS_SORT_ORDER = [
  PROGRAM_STATUS.active,
  PROGRAM_STATUS.assigned,
  PROGRAM_STATUS.paused,
  PROGRAM_STATUS.stopped,
  PROGRAM_STATUS.ended,
];

export default class EproProgram extends Model {
  static entity = "eproPrograms";

  static fields() {
    return {
      id: this.attr(null),
      bundleDetail: this.attr([]),
      programId: this.attr(null),
      programTitle: this.attr(null),
      programDescription: this.attr(null),
      dayStatus: this.attr(null),
      patientId: this.attr(null),
      startsOn: this.attr(null),
      endsOn: this.attr(null),
      therapistUsername: this.attr(null),
      customized: this.attr(false),
      resumeDate: this.attr(null),
      stopDate: this.attr(null),
      pausedDate: this.attr(null),
      patientUsername: this.attr(null),
      stopped: this.attr(false),
      paused: this.attr(false),
    };
  }

  get programStatus() {
    return calculateProgramStatus(this);
  }

  get questionnaires() {
    if (!this.bundleDetail || !this.bundleDetail.length) return [];
    return this.bundleDetail.flatMap((bundle) =>
      bundle.questionnaireDetails.map((q) => ({
        ...q,
        repeat: bundle.repeat,
        bundleId: bundle.bundleId,
        daysForRepeat: bundle.daysForRepeat,
      }))
    );
  }

  static async getPrograms(patientUsername) {
    return await this.api().get(
      `program-management/patient/${patientUsername}`,
      { dataKey: "result" }
    );
  }

  static async updateProgramDuration(programId, type, payload) {
    return await this.api().post(
      `program-management/${programId}/${type}`,
      payload,
      { dataKey: "result" }
    );
  }

  static async updateProgramStatus(programId, status) {
    return await this.api().post(
      `program-management/${programId}/${status}`,
      {},
      { dataKey: "result" }
    );
  }

  static async createProgramForPatient(payload) {
    return await this.api().post("program-management", payload, {
      dataKey: "result",
    });
  }

  static getProgramsSorted(patientUsername) {
    return this.query()
      .where("patientUsername", patientUsername)
      .get()
      .sort((a, b) => {
        const aOrder = STATUS_SORT_ORDER.indexOf(a.programStatus);
        const bOrder = STATUS_SORT_ORDER.indexOf(b.programStatus);
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(b.startsOn) - new Date(a.startsOn);
      });
  }
}

export { calculateProgramStatus };
